import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Lock as LockIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import { contractorAuthService } from '../services/contractorAuthService';
import { getJobByToken } from '../services/contractorJobService';
import BrandMark from '../components/BrandMark';

export const ContractorAuth: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Get job token and contractor email from URL
  const jobToken = searchParams.get('token');
  const prefilledEmail = searchParams.get('email');
  const redirectTo = searchParams.get('redirect') || '/contractor/dashboard';
  
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [checkingContractor, setCheckingContractor] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  
  // Form fields
  const [email, setEmail] = useState(prefilledEmail || '');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    // If we have a job token but no email, fetch the job to get the email
    if (jobToken && !prefilledEmail) {
      fetchJobEmail();
    } else if (prefilledEmail) {
      checkContractorExists(prefilledEmail);
    }
  }, [prefilledEmail, jobToken]);

  // Check contractor exists when email changes (for manual entry)
  useEffect(() => {
    if (email && email !== prefilledEmail && email.includes('@')) {
      // Debounce the check
      const timeoutId = setTimeout(() => {
        checkContractorExists(email);
      }, 500);
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  const fetchJobEmail = async () => {
    if (!jobToken) return;
    
    try {
      const jobData = await getJobByToken(jobToken);
      if (jobData.job.contractorEmail) {
        setEmail(jobData.job.contractorEmail);
        checkContractorExists(jobData.job.contractorEmail);
      }
    } catch (err) {
      console.error('Error fetching job email:', err);
    }
  };

  const checkContractorExists = async (email: string) => {
    try {
      setCheckingContractor(true);
      console.log('Checking contractor exists for:', email);
      const result = await contractorAuthService.checkContractorExists(email);
      console.log('Contractor check result:', result);
      if (result.exists) {
        // If contractor exists but has no password, show registration form
        // If contractor exists and has password, show login form
        setIsLogin(result.hasPassword);
        console.log('Setting isLogin to:', result.hasPassword);
        if (result.contractor) {
          setName(result.contractor.name || '');
          setPhone(result.contractor.phone || '');
        }
      } else {
        setIsLogin(false);
        console.log('Contractor does not exist, setting isLogin to false');
      }
    } catch (err) {
      console.error('Error checking contractor:', err);
    } finally {
      setCheckingContractor(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    // Don't allow submission while checking contractor
    if (checkingContractor) {
      setError('Please wait while we check your account...');
      return;
    }
    
    // Validation
    if (!email || !password) {
      setError('Email and password are required');
      return;
    }
    
    console.log('ContractorAuth handleSubmit - isLogin:', isLogin, 'name:', name);
    
    if (!isLogin && !name) {
      setError('Name is required for registration');
      return;
    }
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    try {
      setLoading(true);
      
      const response = await contractorAuthService.registerOrLogin({
        email,
        name,
        phone,
        password,
        jobToken: jobToken || undefined,
      });

      if (response.success) {
        console.log('✅ Login successful, redirecting to:', redirectTo);
        console.log('Token saved:', !!contractorAuthService.getToken());
        
        // Save token and contractor info
        contractorAuthService.saveToken(response.token);
        contractorAuthService.saveContractorInfo(response.contractor);
        
        // Clear any stale regular auth data
        localStorage.removeItem('user');
        localStorage.removeItem('agency');
        localStorage.removeItem('token');
        
        // Redirect to job portal or dashboard
        navigate(redirectTo);
      }
    } catch (err: any) {
      console.error('Auth error:', err);
      setError(err.response?.data?.error || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4,
        }}
      >
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
              <BrandMark size={40} />
            </Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              🔧 Contractor Portal
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {isLogin ? 'Sign in to access your job' : 'Set up your account to continue'}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            {/* Email */}
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={!!jobToken || !!prefilledEmail}
              required
              sx={{ mb: 2 }}
              helperText={jobToken ? "Email is associated with the job invitation" : ""}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon />
                  </InputAdornment>
                ),
              }}
            />

            {/* Name (for registration) */}
            {!isLogin && (
              <TextField
                fullWidth
                label="Full Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon />
                    </InputAdornment>
                  ),
                }}
              />
            )}

            {/* Phone (optional for registration) */}
            {!isLogin && (
              <TextField
                fullWidth
                label="Phone (Optional)"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon />
                    </InputAdornment>
                  ),
                }}
              />
            )}

            {/* Password */}
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              sx={{ mb: 3 }}
              helperText={!isLogin ? 'Minimum 6 characters' : ''}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Submit Button */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading || checkingContractor}
              sx={{ mb: 2 }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : checkingContractor ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Checking...
                </>
              ) : isLogin ? (
                'Sign In'
              ) : (
                'Set Up Account'
              )}
            </Button>

            {/* Toggle Login/Register */}
            {!jobToken && !prefilledEmail && (
              <Box sx={{ textAlign: 'center' }}>
                <Button
                  onClick={() => {
                    setIsLogin(!isLogin);
                    setError(null);
                  }}
                  disabled={loading}
                >
                  {isLogin
                    ? "Don't have an account? Register"
                    : 'Already have an account? Sign In'}
                </Button>
              </Box>
            )}
          </form>

          {/* Info Box */}
          <Box sx={{ mt: 4, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
            <Typography variant="body2" color="info.contrastText">
              ℹ️ {isLogin 
                ? 'Enter your credentials to access your contractor jobs.' 
                : 'Set up your account to access your contractor jobs and manage your work across multiple agencies.'}
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default ContractorAuth;
